import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { CatalogItem, CatalogSong } from '../../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../catalogArtwork';
import { formatCatalogDay } from '../catalogDates';
import { addSongFlowMessages } from './addSongFlowMessages';
import { dayOf, lyricLineCount } from './addSongFlowSteps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongCardProps {
  /** The saved row; absent on step 1 before the save. */
  item?: CatalogItem;
  song?: CatalogSong;
  /** The title typed on step 1 (the card shows it at once, design a2). */
  draftTitle: string;
  artistName: string;
  today: string;
  /** The "Bio Link" fact (design smartlink-editor e1, e2). Undefined: no fact. */
  bioLinkFact?: 'onSave' | 'added';
}

interface Fact {
  key: string;
  label: string;
  value: string | null;
  isNew?: boolean;
}

/**
 * The song card (design catalog-add-song a0): it grows with every answer. Below 900 px it folds into
 * a strip with the cover and the title (design g2).
 */
const SongCardComponent: React.FC<SongCardProps> = ({ item, song, draftTitle, artistName, today, bioLinkFact }) => {
  const intl = useIntl();
  const title = item?.title ?? draftTitle.trim();
  const artworkSrc = item === undefined ? undefined : catalogArtworkSrc(item, 96);
  const isOut = item?.kind === CatalogItemKind.claimed;

  const pill = useMemo(() => {
    if (item === undefined) {
      return { text: intl.formatMessage(addSongFlowMessages.pillNew), className: styles.pillDraft };
    }
    if (isOut) {
      return { text: intl.formatMessage(addSongFlowMessages.pillOut), className: styles.pillOut };
    }
    if (item.releaseDate !== null && dayOf(item.releaseDate) >= today) {
      return { text: intl.formatMessage(addSongFlowMessages.pillUpcoming), className: styles.pillUp };
    }
    return { text: intl.formatMessage(addSongFlowMessages.pillDraft), className: styles.pillDraft };
  }, [intl, isOut, item, today]);

  const facts = useMemo<Fact[]>(() => {
    let release: string | null = null;
    if (item?.releaseDate !== null && item?.releaseDate !== undefined) {
      release = formatCatalogDay(intl, item.releaseDate);
    } else if (isOut) {
      release = intl.formatMessage(addSongFlowMessages.cardOut);
    }
    let audio: string | null = null;
    if (song?.master !== null && song?.master !== undefined) {
      audio =
        song.master.status === 'processing'
          ? intl.formatMessage(addSongFlowMessages.cardReading)
          : intl.formatMessage(addSongFlowMessages.cardReady);
    } else if (item?.song.hasMaster === true) {
      audio = intl.formatMessage(addSongFlowMessages.cardAdded);
    }
    const lyrics = song?.lyrics ?? null;
    const services =
      item?.serviceLinks === null || item?.serviceLinks === undefined ? 0 : Object.keys(item.serviceLinks).length;
    const list: Fact[] = [
      {
        key: 'release',
        label: intl.formatMessage(isOut ? addSongFlowMessages.cardReleased : addSongFlowMessages.cardRelease),
        value: release,
      },
      { key: 'isrc', label: intl.formatMessage(addSongFlowMessages.cardIsrc), value: song?.isrc ?? null },
      { key: 'upc', label: intl.formatMessage(addSongFlowMessages.cardUpc), value: song?.upc ?? null },
    ];
    if (services > 0) {
      list.push({
        key: 'services',
        label: intl.formatMessage(addSongFlowMessages.cardServices),
        value: String(services),
      });
    }
    list.push(
      {
        key: 'cover',
        label: intl.formatMessage(addSongFlowMessages.cardCover),
        value: artworkSrc === undefined ? null : intl.formatMessage(addSongFlowMessages.cardAdded),
      },
      { key: 'audio', label: intl.formatMessage(addSongFlowMessages.cardAudio), value: audio },
      {
        key: 'lyrics',
        label: intl.formatMessage(addSongFlowMessages.cardLyrics),
        value:
          lyrics === null || lyrics.trim() === ''
            ? null
            : intl.formatMessage(addSongFlowMessages.cardLyricsLines, { count: lyricLineCount(lyrics) }),
      }
    );
    if (bioLinkFact !== undefined) {
      list.push({
        key: 'bioLink',
        label: intl.formatMessage(addSongFlowMessages.cardBioLink),
        value: intl.formatMessage(
          bioLinkFact === 'added' ? addSongFlowMessages.cardBioLinkAdded : addSongFlowMessages.cardBioLinkOnSave
        ),
        isNew: true,
      });
    }
    return list;
  }, [artworkSrc, bioLinkFact, intl, isOut, item, song]);

  return (
    <aside
      className={styles.card}
      aria-label={intl.formatMessage(addSongFlowMessages.cardTitle)}
      data-testid="add-song-card"
    >
      <div className={styles.cardHead}>
        <span>{intl.formatMessage(addSongFlowMessages.cardTitle)}</span>
        <span className={clsx(styles.pill, pill.className)}>{pill.text}</span>
      </div>
      <div className={clsx(styles.art, artworkSrc === undefined && styles.artEmpty)}>
        {artworkSrc === undefined ? (
          intl.formatMessage(addSongFlowMessages.cardCoverLater)
        ) : (
          <img src={artworkSrc} alt="" />
        )}
      </div>
      <div className={styles.cardIdentity}>
        <div
          className={clsx(styles.cardTitle, title === '' && styles.cardTitleEmpty)}
          title={title === '' ? undefined : title}
          data-testid="add-song-card-title"
        >
          {title === '' ? intl.formatMessage(addSongFlowMessages.cardUntitled) : title}
        </div>
        <div className={styles.cardArtist}>{item?.artistName ?? artistName}</div>
      </div>
      <dl className={styles.facts}>
        {facts.map((fact) => (
          <div
            key={fact.key}
            className={clsx(styles.fact, fact.value === null && styles.factNone, fact.isNew === true && styles.factNew)}
          >
            <dt>{fact.label}</dt>
            <dd>
              {fact.value ??
                intl.formatMessage(
                  ['audio', 'cover', 'lyrics'].includes(fact.key)
                    ? addSongFlowMessages.cardNotAdded
                    : addSongFlowMessages.cardNotSet
                )}
            </dd>
          </div>
        ))}
      </dl>
      <div className={styles.cardFoot}>
        <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} />
        <span>
          {intl.formatMessage(
            item === undefined ? addSongFlowMessages.cardSaveNote : addSongFlowMessages.cardSavedNote
          )}
        </span>
      </div>
    </aside>
  );
};

export const SongCard = TypedMemo(SongCardComponent);
